import React from 'react';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import EmptyTableState from 'components/EmptyTableState';
import LoadingSpinner from 'components/LoadingSpinner';
import { TagChip } from 'components/TagsRow';
import { TAG_STRINGS } from 'components/TagManager/constants';
import useTagsData from 'hooks/useTagsData';
import NoTagsIcon from 'assets/no_tags_found.svg';
import CROP_ZONE_TAGS_STRINGS from './constants';

// The artwork's natural size; scaling it up would not match the design.
const EMPTY_STATE_ICON_WIDTH = 206;

interface CropZoneTagsTabProps {
  tagIds?: string[];
  onEdit: () => void;
}

const CropZoneTagsTab: React.FC<CropZoneTagsTabProps> = ({
  tagIds,
  onEdit
}) => {
  const { tags, loading } = useTagsData();

  if (loading) {
    return <LoadingSpinner message={TAG_STRINGS.loadingMessage} />;
  }

  // Resolved against the live catalogue rather than any cached copy, so a tag
  // deleted organisation-wide stops resolving instead of lingering as a stale chip.
  const resolvedTags = (tagIds ?? [])
    .map(id => tags.find(tag => tag.id === id))
    .filter((tag): tag is typeof tags[number] => Boolean(tag))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (resolvedTags.length === 0) {
    return (
      <div className="mb-10">
        <EmptyTableState
          title={TAG_STRINGS.emptyStateTitle}
          overlayIcon={NoTagsIcon}
          message={CROP_ZONE_TAGS_STRINGS.viewEmptyStateMessage}
          imageWidth={EMPTY_STATE_ICON_WIDTH}
          containerClassName="w-full flex flex-col justify-center items-center pt-12px"
          titleClassName="text-sm font-semibold mb-1"
          button={
            <Button
              type={ButtonType.primary}
              size="large"
              style={{ fontSize: 14 }}
              onClick={onEdit}
            >
              {CROP_ZONE_TAGS_STRINGS.editCropZoneInformation}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      className="w-full flex flex-wrap gap-2"
      data-testid="crop-zone-tags-list"
    >
      {resolvedTags.map(tag => (
        <TagChip key={tag.id} tag={tag} truncate />
      ))}
    </div>
  );
};

export default CropZoneTagsTab;
